import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';

import { useInboundCrossDockServiceFee } from 'components/logistics/components/crossDockPurchaseOrders/useInboundCrossDockServiceFee';
import {
    addOptionalAdditionalFeeRow,
    applyInboundCrossDockServiceFeeRate,
    buildDefaultServiceFeeRow,
    getActiveAdditionalFees,
    getRequiredServiceFeeRow,
    removeAdditionalFeeRow,
    sumAdditionalFeeTotals,
    updateAdditionalFeeRow,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFees';
import {
    getAdditionalFeeChargeTypeLabel,
    getOptionalAdditionalFeeTypeOptions,
    getRequiredAdditionalFeeChargeType,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFeeTypeUtils';
import { formatCurrency } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const EMPTY_REMOVED_FEE_CHARGE_TYPES = new Set();

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: Object) => Object) => void} props.onDraftChange
 * @param {Array<{ chargeType: string, label: string, required: boolean }>} props.additionalFeeTypes
 * @param {boolean} [props.isLoadingFeeTypes]
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.feeErrors]
 * @param {Object} [props.feeFieldErrors]
 * @param {boolean} [props.softRemovePersistedFees=false] When true, persisted fee rows (with `id`) are marked for removal instead of deleted immediately.
 * @param {Set<string>} [props.removedFeeChargeTypes]
 * @param {(updater: Set<string>|((prev: Set<string>) => Set<string>)) => void} [props.onRemovedFeeChargeTypesChange]
 */
export function CrossDockPurchaseOrderAdditionalFeesCard({
    additionalFeeTypes = [],
    draft,
    feeErrors = [],
    feeFieldErrors = {},
    isLoadingFeeTypes = false,
    onDraftChange,
    onRemovedFeeChargeTypesChange,
    removedFeeChargeTypes,
    showValidation = false,
    softRemovePersistedFees = false,
}) {
    const [selectedChargeType, setSelectedChargeType] = useState('');
    const lastAppliedCrossDockIdRef = useRef(null);
    const initialPersistedCrossDockIdRef = useRef(null);
    const didSkipPersistedHydrationServiceFeeRef = useRef(false);
    const prevSoftRemovePersistedFeesRef = useRef(softRemovePersistedFees);

    const requiredChargeType = getRequiredAdditionalFeeChargeType(additionalFeeTypes);
    const requiredFeeLabel = requiredChargeType
        ? getAdditionalFeeChargeTypeLabel(additionalFeeTypes, requiredChargeType)
        : 'Service Fee';

    const additionalFees = draft.additionalFees ?? [];
    const hasPersistedFeeRows = additionalFees.some((fee) => fee.id != null);
    // Use isLoading (not isFetching) so a background refetch does not block applying a cached serviceFee.
    const { data: inboundServiceFee = 0, isLoading: isServiceFeeLoading } = useInboundCrossDockServiceFee(
        draft.crossDockId,
    );

    useEffect(() => {
        if (!requiredChargeType) {
            return;
        }
        onDraftChange((prev) => {
            if (getRequiredServiceFeeRow(prev.additionalFees, requiredChargeType)) {
                return prev;
            }
            return {
                ...prev,
                additionalFees: [buildDefaultServiceFeeRow(requiredChargeType), ...(prev.additionalFees ?? [])],
            };
        });
    }, [onDraftChange, requiredChargeType]);

    useEffect(() => {
        if (softRemovePersistedFees && !prevSoftRemovePersistedFeesRef.current) {
            initialPersistedCrossDockIdRef.current = null;
            didSkipPersistedHydrationServiceFeeRef.current = false;
            lastAppliedCrossDockIdRef.current = null;
        }
        prevSoftRemovePersistedFeesRef.current = softRemovePersistedFees;
    }, [softRemovePersistedFees]);

    useEffect(() => {
        if (!hasPersistedFeeRows) {
            initialPersistedCrossDockIdRef.current = null;
            didSkipPersistedHydrationServiceFeeRef.current = false;
            return;
        }
        if (initialPersistedCrossDockIdRef.current !== null) {
            return;
        }
        const crossDockId = Number(draft.crossDockId);
        if (Number.isFinite(crossDockId) && crossDockId > 0) {
            initialPersistedCrossDockIdRef.current = crossDockId;
        }
    }, [draft.crossDockId, hasPersistedFeeRows]);

    useEffect(() => {
        const crossDockId = Number(draft.crossDockId);
        if (!requiredChargeType || !Number.isFinite(crossDockId) || crossDockId <= 0) {
            return;
        }
        if (isServiceFeeLoading) {
            return;
        }
        if (lastAppliedCrossDockIdRef.current === crossDockId) {
            return;
        }
        if (
            hasPersistedFeeRows &&
            !didSkipPersistedHydrationServiceFeeRef.current &&
            initialPersistedCrossDockIdRef.current !== null &&
            crossDockId === initialPersistedCrossDockIdRef.current
        ) {
            didSkipPersistedHydrationServiceFeeRef.current = true;
            lastAppliedCrossDockIdRef.current = crossDockId;
            return;
        }
        lastAppliedCrossDockIdRef.current = crossDockId;
        onDraftChange((prev) => applyInboundCrossDockServiceFeeRate(prev, inboundServiceFee, requiredChargeType));
    }, [
        draft.crossDockId,
        hasPersistedFeeRows,
        inboundServiceFee,
        isServiceFeeLoading,
        onDraftChange,
        requiredChargeType,
    ]);

    const availableChargeTypes = useMemo(() => {
        const used = new Set(additionalFees.map((fee) => fee.chargeType));
        return getOptionalAdditionalFeeTypeOptions(additionalFeeTypes).filter((type) => !used.has(type.chargeType));
    }, [additionalFeeTypes, additionalFees]);

    const optionalFeeTypeLabels = getOptionalAdditionalFeeTypeOptions(additionalFeeTypes)
        .map((type) => type.label)
        .join(', ');

    const removedFees = removedFeeChargeTypes ?? EMPTY_REMOVED_FEE_CHARGE_TYPES;
    const activeAdditionalFees = useMemo(
        () => getActiveAdditionalFees(additionalFees, removedFees),
        [additionalFees, removedFeeChargeTypes],
    );
    const totalCharges = sumAdditionalFeeTotals(activeAdditionalFees);

    const getFieldError = useCallback(
        (field) => {
            if (!showValidation) return '';
            return feeFieldErrors?.[field] ?? '';
        },
        [feeFieldErrors, showValidation],
    );

    const handleFeeFieldChange = useCallback(
        (chargeType, field) => (event) => {
            const value = event?.target?.value ?? '';
            onDraftChange((prev) => ({
                ...prev,
                additionalFees: updateAdditionalFeeRow(prev.additionalFees, chargeType, { [field]: value }),
            }));
        },
        [onDraftChange],
    );

    const handleAddFee = useCallback(() => {
        if (!selectedChargeType) {
            return;
        }
        onDraftChange((prev) => ({
            ...prev,
            additionalFees: addOptionalAdditionalFeeRow(prev.additionalFees, selectedChargeType),
        }));
        setSelectedChargeType('');
    }, [onDraftChange, selectedChargeType]);

    const togglePersistedFeeRemoval = useCallback(
        (chargeType) => {
            if (typeof onRemovedFeeChargeTypesChange !== 'function') {
                return;
            }
            onRemovedFeeChargeTypesChange((prev) => {
                const next = new Set(prev);
                if (next.has(chargeType)) {
                    next.delete(chargeType);
                } else {
                    next.add(chargeType);
                }
                return next;
            });
        },
        [onRemovedFeeChargeTypesChange],
    );

    const handleRemoveFee = useCallback(
        (fee) => {
            const { chargeType } = fee;
            const isPersistedRow = fee.id != null;
            if (softRemovePersistedFees && isPersistedRow) {
                togglePersistedFeeRemoval(chargeType);
                return;
            }
            onDraftChange((prev) => ({
                ...prev,
                additionalFees: removeAdditionalFeeRow(prev.additionalFees, chargeType, requiredChargeType),
            }));
        },
        [onDraftChange, requiredChargeType, softRemovePersistedFees, togglePersistedFeeRemoval],
    );

    const feeListError = getFieldError('additionalFees');

    if (isLoadingFeeTypes) {
        return (
            <Card>
                <CardHeader className="pb-0">
                    <CardTitle>Additional Fees</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading fee types…</span>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Additional Fees</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-6 pt-6">
                {showValidation && feeErrors.length > 0 ? (
                    <ul className="list-disc pl-5 text-sm text-destructive">
                        {feeErrors.map((message) => (
                            <li key={message}>{message}</li>
                        ))}
                    </ul>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        <Label className={labelClassName}>Additional Fee</Label>
                        <Select
                            value={selectedChargeType}
                            onValueChange={setSelectedChargeType}
                            disabled={availableChargeTypes.length === 0}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select fee type" />
                            </SelectTrigger>
                            <SelectContent>
                                {availableChargeTypes.map((type) => (
                                    <SelectItem key={type.chargeType} value={type.chargeType}>
                                        {type.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button type="button" variant="secondary" onClick={handleAddFee} disabled={!selectedChargeType}>
                        Add
                    </Button>
                </div>

                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Charge Type</TableHead>
                                <TableHead className="w-[120px]">Quantity</TableHead>
                                <TableHead className="w-[140px]">Rate</TableHead>
                                <TableHead className="w-[140px]">Total Amount</TableHead>
                                <TableHead className="w-[72px] text-xs font-semibold uppercase tracking-wide">
                                    Remove
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {additionalFees.map((fee) => {
                                const quantityError = getFieldError(`fee.${fee.chargeType}.quantity`);
                                const unitRateError = getFieldError(`fee.${fee.chargeType}.unitRate`);
                                const isRequiredFee = fee.chargeType === requiredChargeType;
                                const isMarkedForRemoval =
                                    softRemovePersistedFees && fee.id != null && removedFees.has(fee.chargeType);
                                const removedCellClassName = isMarkedForRemoval
                                    ? 'text-destructive line-through opacity-80'
                                    : '';

                                return (
                                    <TableRow
                                        key={fee.chargeType}
                                        className={isMarkedForRemoval ? 'bg-destructive/10' : undefined}
                                    >
                                        <TableCell className={cn('font-medium', removedCellClassName)}>
                                            {getAdditionalFeeChargeTypeLabel(additionalFeeTypes, fee.chargeType)}
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="number"
                                                inputMode="numeric"
                                                min="1"
                                                step="1"
                                                value={fee.quantity ?? ''}
                                                onChange={handleFeeFieldChange(fee.chargeType, 'quantity')}
                                                aria-invalid={Boolean(quantityError)}
                                                disabled={isMarkedForRemoval}
                                                className={removedCellClassName}
                                            />
                                            {quantityError ? (
                                                <p className="mt-1 text-xs text-destructive">{quantityError}</p>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                min="0"
                                                step="0.01"
                                                value={fee.unitRate ?? ''}
                                                onChange={handleFeeFieldChange(fee.chargeType, 'unitRate')}
                                                aria-invalid={Boolean(unitRateError)}
                                                disabled={isMarkedForRemoval}
                                                className={removedCellClassName}
                                            />
                                            {unitRateError ? (
                                                <p className="mt-1 text-xs text-destructive">{unitRateError}</p>
                                            ) : null}
                                        </TableCell>
                                        <TableCell className={removedCellClassName}>
                                            {formatCurrency(fee.totalRate)}
                                        </TableCell>
                                        <TableCell>
                                            {isRequiredFee ? null : (
                                                <Button
                                                    type="button"
                                                    variant={isMarkedForRemoval ? 'destructive' : 'ghost'}
                                                    size="iconSm"
                                                    onClick={() => handleRemoveFee(fee)}
                                                    aria-label={
                                                        isMarkedForRemoval
                                                            ? 'Unmark removal'
                                                            : `Mark ${getAdditionalFeeChargeTypeLabel(additionalFeeTypes, fee.chargeType)} for removal`
                                                    }
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={3} className="text-right font-semibold">
                                    Total Charges
                                </TableCell>
                                <TableCell className="font-semibold">{formatCurrency(totalCharges)}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </div>

                {feeListError ? <p className="text-xs text-destructive">{feeListError}</p> : null}

                <p className="text-xs text-muted-foreground">
                    Optional charges: {optionalFeeTypeLabels || '—'}. The {requiredFeeLabel} is calculated from the
                    pallet count and is always included.
                </p>
            </CardContent>
        </Card>
    );
}

CrossDockPurchaseOrderAdditionalFeesCard.displayName = 'CrossDockPurchaseOrderAdditionalFeesCard';
